import React, {useContext, useEffect, useState} from 'react';
import "./Signin.scss";
import {Link, useNavigate} from "react-router-dom";
import createLink from "../../../libs/createLink";
import _ from "lodash";
import {useMutation} from "@tanstack/react-query";
import {loginUser, sendMfaLoginEmailCode, verifyMfaLogin, verifyTotpLogin} from "../../../api/public-voissa/users";
import useQueryCurrentUser from "../../../queries/currentUser";
import qs from "qs";
import Drawer from "../Drawer/Drawer";
import Button, {BUTTON_VARIANTS} from "../UI/Button/Button";
import Group from '../UI/Group/Group';
import Container from '../UI/Container/Container';
import { Helmet } from 'react-helmet';
import { canonical } from '../../../libs/helper';
import { config } from '../../../config';
import Input from '../UI/Input/Input';
import TextError from '../UI/TextError/TextError';
import { ModalContext } from '../ModalManager';
import {ACTION_LOCKED} from "../../../hooks/useCurrentUserActionLocked";
import { toastError, toastSuccess } from '../../../libs/toast';


const SIGNIN_SUBTITLE_LOCKED_MAP = {
    DEFAULT : ()=>"Connectez-vous à votre compte Voissa",
    SEARCH_MEMBERS : ()=>"Connectez-vous pour rechercher des membres",
    [ACTION_LOCKED.CREATE_LIKE] : (lockedContentOwner) => <>Connectez-vous pour aimer la publication de <b>{lockedContentOwner?.username}</b></>,
    [ACTION_LOCKED.CREATE_FOLLOW] : (lockedContentOwner) => <>Connectez-vous pour suivre le profil de <b>{lockedContentOwner?.username}</b></>,
    [ACTION_LOCKED.CREATE_FRIEND] : (lockedContentOwner) => <>Connectez-vous pour ajouter <b>{lockedContentOwner?.username}</b> à votre cercle</>,
    [ACTION_LOCKED.MESSENGER] : (lockedContentOwner) => <>Connectez-vous pour discuter avec <b>{lockedContentOwner?.username}</b></>,
    VIEW_PUBLICATION: (lockedContentOwner)=><>Connectez-vous pour voir la publication de <b>{lockedContentOwner?.username}</b></>,
    FILTER_MEMBERS: ()=><>Connectez-vous pour pouvoir filtrer les membres</>,
    PAGE_MEMBERS: ()=><>Connectez-vous pour accéder à la page des membres</>
}

const initDataUser = {
    login : "",
    password : "",
    error : false,
    showPassword : false
}

const SigninForm = ({
        lockedReason,
        lockedContentOwner,
        lockedAction
    }) => {

    const [user, setUser] = useState(initDataUser);
    const navigate = useNavigate();

    const {openModal} = useContext(ModalContext);

    // TOTP step state
    const [mfaStep, setMfaStep] = useState(false);
    const [mfaMethod, setMfaMethod] = useState(null);
    const [mfaCode, setMfaCode] = useState(Array(6).fill(''));
    const [mfaChallengeId, setMfaChallengeId] = useState(null);
    const [mfaAvailableMethods, setMfaAvailableMethods] = useState([]);
    const [mfaEmailMasked, setMfaEmailMasked] = useState('');
    const [mfaEmailSent, setMfaEmailSent] = useState(false);
    const [mfaEmailCooldown, setMfaEmailCooldown] = useState(0);
    const [mfaReason, setMfaReason] = useState(null);   // 'setting' | 'suspicious'

    // Legacy TOTP step token
    const [ephemeralToken, setEphemeralToken] = useState(null);

    const mfaCodeLength = 6;
    const MFA_EMAIL_RESEND_DELAY = 30;

    function resetMfaCode() {
        setMfaCode(Array(mfaCodeLength).fill(''));
    }

    function resetMfaState() {
        setMfaStep(false);
        setMfaMethod(null);
        resetMfaCode();
        setEphemeralToken(null);
        setMfaChallengeId(null);
        setMfaAvailableMethods([]);
        setMfaEmailMasked('');
        setMfaEmailSent(false);
        setMfaEmailCooldown(0);
        setMfaReason(null);
    }

    function switchMfaMethod(method) {
        setMfaMethod(method);
        resetMfaCode();
    }

    function focusMfaCodeInput(index) {
        const input = document.getElementById(`signin-mfa-code-${index}`);
        if (input) input.focus();
    }

    function handleMfaCodeChange(index, value) {
        const digit = String(value || '').replace(/\D/g, '').slice(-1);
        const next = [...mfaCode];
        next[index] = digit;
        setMfaCode(next);

        if (digit && index < mfaCodeLength - 1) {
            focusMfaCodeInput(index + 1);
        }
    }

    function handleMfaCodeKeyDown(index, event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!isLoadingMfa && !mfaCode.some((d) => !d)) {
                mutateMfaVerify();
            }
            return;
        }
        if (event.key === 'Backspace' && !mfaCode[index] && index > 0) {
            focusMfaCodeInput(index - 1);
        }
        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            focusMfaCodeInput(index - 1);
        }
        if (event.key === 'ArrowRight' && index < mfaCodeLength - 1) {
            event.preventDefault();
            focusMfaCodeInput(index + 1);
        }
    }

    function handleMfaCodePaste(event) {
        const pasted = String(event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, mfaCodeLength);
        if (!pasted) return;
        event.preventDefault();

        const next = Array(mfaCodeLength).fill('');
        pasted.split('').forEach((char, i) => {
            next[i] = char;
        });
        setMfaCode(next);

        const targetIndex = Math.min(pasted.length, mfaCodeLength) - 1;
        if (targetIndex >= 0) {
            focusMfaCodeInput(targetIndex);
        }
    }

    const [shouldFetchCurrentUser, setShouldFetchCurrentUser] = useState(false);

    const {isLoading, error, isError, mutate } = useMutation(data => loginUser(data),{
        onError : (error) =>{
            if (!!_.get(error, 'errors.passwordWeak')) navigate('/change-password', {state: {email:_.get(error, "errors.email")}});
        },
        onSuccess : (data) =>{
            if (data?.requiresMfa) {
                setMfaStep(true);
                setMfaChallengeId(data.challengeId || null);
                setMfaAvailableMethods(Array.isArray(data.availableMethods) ? data.availableMethods : []);
                setMfaEmailMasked(data.emailMasked || '');
                setMfaReason(data.mfaReason || null);
                resetMfaCode();
                setMfaEmailSent(false);
                setMfaEmailCooldown(0);

                const methods = Array.isArray(data.availableMethods) ? data.availableMethods : [];
                const preferredMethod = methods.includes('totp') ? 'totp' : (methods.includes('email') ? 'email' : null);
                setMfaMethod(preferredMethod);
                return;
            }

            // Si le serveur demande une vérification TOTP
            if (data?.requireTotp) {
                setEphemeralToken(data.ephemeralToken || null);
                setMfaStep(true);
                setMfaMethod('totp');
                resetMfaCode();
                setMfaEmailSent(false);
                setMfaEmailCooldown(0);
                return;
            }
            setShouldFetchCurrentUser(true);
        }
    });

    const { isLoading: isLoadingMfa, error: mfaError, isError: isMfaError, mutate: mutateMfaVerify } = useMutation(
        () => {
            if (mfaChallengeId) {
                return verifyMfaLogin({
                    challengeId: mfaChallengeId,
                    code: mfaCode.join(''),
                });
            }
            return verifyTotpLogin({ code: mfaCode.join(''), ephemeralToken });
        },
        {
            onSuccess: () => {
                setShouldFetchCurrentUser(true);
            }
        }
    );

    const { isLoading: isLoadingSendMfaEmail, mutate: mutateSendMfaEmail } = useMutation(
        () => sendMfaLoginEmailCode({ challengeId: mfaChallengeId }),
        {
            onSuccess: () => {
                setMfaEmailSent(true);
                setMfaEmailCooldown(MFA_EMAIL_RESEND_DELAY);
                toastSuccess('Un code a été envoyé par email.');
            },
            onError: () => {
                toastError("Impossible d'envoyer le code email.");
            }
        }
    );

    useEffect(() => {
        if (!mfaEmailCooldown) return;
        const timer = setInterval(() => {
            setMfaEmailCooldown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(timer);
    }, [mfaEmailCooldown]);

    useQueryCurrentUser({enabled : shouldFetchCurrentUser, onSuccess : (currentUser) =>{
            if(currentUser?._id) {
                if (currentUser.role === "emailUnconfirmed" && currentUser.profile.isCompleted) {
                    let {aToken} = qs.parse(window.location.search, {ignoreQueryPrefix: true});
                    if(!aToken && window.location.pathname !== createLink("userMe", currentUser)){
                        sessionStorage.setItem(`${process.env.REACT_APP_TOKEN_NAME}_alert`, true);
                        navigate(createLink("userMe", currentUser));
                    }
                }
                else if (!currentUser.profile.isCompleted) {
                    let {aToken} = qs.parse(window.location.search, {ignoreQueryPrefix: true});
                    if(!aToken && window.location.pathname !== '/first-connexion'){
                        navigate('/first-connexion');
                    }
                }
                else{
                    window.location.reload();
                    // navigate(_redirectTo());
                    // return <Navigate to={_redirectTo()} />
                }
            }
        }});


    // const _redirectTo = () => {
    //     let currentDomain = window.location.hostname;
    //     let referrer = document.referrer;
    //     let redirection = '/';
    //     if (referrer.indexOf(currentDomain) >= 0 && referrer.indexOf(createLink("signin")) <= 0) {
    //         redirection = referrer.replace(window.location.origin, "");
    //     }
    //     return redirection;
    // }


    function _updateFieldUser(key, value){
        setUser({...user, [key] : value});
    }

    function _onSubmit(event){
        event.preventDefault();
        event.stopPropagation();
        let error = !(user.login.length && user.password.length);
        if(!error){
            mutate({
                login: user.login,
                password: user.password,
            });
        }
        else{
            setUser({...user, error});
        }
    }

    const connectionError = isError && error.statusCode === 401;
    const rateLimitError = isError && error.statusCode === 429;
    const banError = isError && error.statusCode === 400 && _.get(error, "errors.isBan");
    const mfaInvalidError = isMfaError && [400, 401].includes(mfaError?.statusCode || mfaError?.status);
    const isMfaCodeIncomplete = mfaCode.some((digit) => !digit);

    // Étape MFA : choix méthode + saisie du code
    if (mfaStep) {
        const showMethodChoice = !!mfaChallengeId && mfaAvailableMethods.includes('totp') && mfaAvailableMethods.includes('email');
        const hasTotpMethod = mfaAvailableMethods.includes('totp') || !mfaChallengeId;
        const hasEmailMethod = !!mfaChallengeId && mfaAvailableMethods.includes('email');
        const showEmailCodeInput = mfaMethod === 'email' && (mfaEmailSent || !mfaChallengeId);
        const mfaSubtitle = mfaMethod === 'totp'
            ? "Entrez le code à 6 chiffres affiché dans votre application d'authentification."
            : showEmailCodeInput
                ? <>Saisissez le code envoyé à <b>{mfaEmailMasked}</b> pour vous connecter.</>
                : <>Un code va être envoyé à <b>{mfaEmailMasked}</b>.</>;

        return (
            <div id="signin">
                <div className="signin-title">Vérification</div>
                {!mfaMethod ? (
                    <div className="signin-subtitle">Aucune méthode MFA disponible.</div>
                ) : (
                <form className="signin-mfa-form" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); mutateMfaVerify(); }} noValidate>
                    {mfaReason === 'suspicious' && !showEmailCodeInput && (
                        <div className="signin-mfa-alert">Activité inhabituelle détectée sur votre compte. Veuillez confirmer votre identité.</div>
                    )}
                    <div className="signin-subtitle">{mfaSubtitle}</div>
                    {mfaMethod === 'totp' ? (
                        <>
                            <div className="signin-mfa-code" onPaste={handleMfaCodePaste}>
                                {mfaCode.map((digit, index) => (
                                    <Input
                                        key={index}
                                        id={`signin-mfa-code-${index}`}
                                        type="text"
                                        value={digit}
                                        onChange={(e) => handleMfaCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleMfaCodeKeyDown(index, e)}
                                        maxLength={1}
                                        autoComplete="one-time-code"
                                        inputMode="numeric"
                                        autoFocus={index === 0}
                                        className="signin-mfa-code-input"
                                    />
                                ))}
                            </div>
                            <div className="signin-mfa-hint">Vous pouvez coller directement les 6 chiffres.</div>
                            <TextError show={mfaInvalidError} center>Code invalide. Veuillez réessayer.</TextError>
                            <Button variant={BUTTON_VARIANTS.REVERSE} type="submit" loading={isLoadingMfa} disabled={isMfaCodeIncomplete || isLoadingMfa} full big className="signin-mfa-primary">
                                Valider le code
                            </Button>
                            {showMethodChoice && hasEmailMethod ? (
                                <Group center className="signin-mfa-secondary">
                                    <Button variant={BUTTON_VARIANTS.CLEAR} type="button" onClick={() => switchMfaMethod('email')}>
                                        Utiliser l'e-mail à la place
                                    </Button>
                                </Group>
                            ) : null}
                        </>
                    ) : (
                        <>
                            {!showEmailCodeInput ? (
                                <Button
                                    variant={BUTTON_VARIANTS.REVERSE}
                                    type="button"
                                    onClick={() => mutateSendMfaEmail()}
                                    loading={isLoadingSendMfaEmail}
                                    full
                                    big
                                    className="signin-mfa-primary"
                                >
                                    Envoyer le code
                                </Button>
                            ) : (
                                <>
                                    <div className="signin-mfa-code" onPaste={handleMfaCodePaste}>
                                        {mfaCode.map((digit, index) => (
                                            <Input
                                                key={index}
                                                id={`signin-mfa-code-${index}`}
                                                type="text"
                                                value={digit}
                                                onChange={(e) => handleMfaCodeChange(index, e.target.value)}
                                                onKeyDown={(e) => handleMfaCodeKeyDown(index, e)}
                                                maxLength={1}
                                                autoComplete="one-time-code"
                                                inputMode="numeric"
                                                autoFocus={index === 0}
                                                className="signin-mfa-code-input"
                                            />
                                        ))}
                                    </div>
                                    <div className="signin-mfa-hint">Vous pouvez coller directement les 6 chiffres.</div>
                                    <TextError show={mfaInvalidError} center>Code invalide. Veuillez réessayer.</TextError>
                                    <Button variant={BUTTON_VARIANTS.REVERSE} type="submit" loading={isLoadingMfa} disabled={isMfaCodeIncomplete || isLoadingMfa} full big className="signin-mfa-primary">
                                        Valider le code
                                    </Button>
                                    {mfaEmailCooldown > 0 ? (
                                        <div className="signin-mfa-meta">Nouveau renvoi possible dans {mfaEmailCooldown}s</div>
                                    ) : (
                                        <Group center className="signin-mfa-secondary">
                                            <Button
                                                variant={BUTTON_VARIANTS.CLEAR}
                                                type="button"
                                                onClick={() => mutateSendMfaEmail()}
                                                loading={isLoadingSendMfaEmail}
                                            >
                                                Je n'ai pas reçu le code ? Renvoyer
                                            </Button>
                                        </Group>
                                    )}

                                </>
                            )}
                            {showMethodChoice && hasTotpMethod ? (
                                <Group center className="signin-mfa-secondary">
                                    <Button variant={BUTTON_VARIANTS.CLEAR} type="button" onClick={() => switchMfaMethod('totp')}>
                                        Revenir à l'authentificateur
                                    </Button>
                                </Group>
                            ) : null}
                        </>
                    )}

                    <Group center className="signin-mfa-back">
                        <Button variant={BUTTON_VARIANTS.CLEAR} type="button" onClick={resetMfaState}>
                            Retour à la connexion
                        </Button>
                    </Group>
                </form>
                )}
            </div>
        );
    }

    return(
        <div id="signin">
            <div className="signin-title">Connexion</div>
            <div className="signin-subtitle">
                {SIGNIN_SUBTITLE_LOCKED_MAP[lockedAction] ? SIGNIN_SUBTITLE_LOCKED_MAP[lockedAction](lockedContentOwner) : SIGNIN_SUBTITLE_LOCKED_MAP.DEFAULT()}
            </div>
            
            <form onSubmit={(e) => _onSubmit(e)} autoComplete="on" noValidate>
                <Input 
                    placeholder="Email ou pseudo"
                    name="login"
                    type="text"
                    full
                    className="signin-input"
                    autoComplete="username"
                    value={user.login}
                    onChange={e => _updateFieldUser("login", e.target.value)}
                />
                <Input
                    type="password"
                    placeholder="Mot de passe"
                    name="password" 
                    full
                    autoComplete="current-password" 
                    className="signin-input"
                    value={user.password}
                    onChange={e => _updateFieldUser("password", e.target.value)}
                />
                <Link className='signin-forget-pass' to={createLink("forgotPassword")}>Mot de passe oublié ?</Link>

                <TextError show={connectionError} center>Identifiant ou mot de passe invalide</TextError>
                <TextError show={rateLimitError} center>Trop de demandes, veuillez réessayer dans 10 minutes</TextError>
                <TextError show={banError} center>
                    Votre profil a été banni de Voissa {error && error.message !== "Bad Request" ? `pour la raison suivante "${error.message}"` : ""}<br/>
                    Pour toute question veuillez <Link to={createLink("contact")}>nous contacter</Link>
                </TextError>
                <Button variant={BUTTON_VARIANTS.REVERSE} type="submit" loading={isLoading} full big style={{marginBottom:'8px', marginTop:'16px'}}>
                    Connexion
                </Button>
                <Button variant={BUTTON_VARIANTS.OUTLINE} onClick={()=>openModal('Signup')} big full>
                    Créer un compte
                </Button>
            </form>
            
        </div>
    )
}

export const Signin = ({
    toggle = false,
    handleClose = () =>{},
    lockedReason,
    lockedContentOwner,
    lockedAction
 }) => {

    return (
        <Drawer
            open={toggle}
            handleClose={handleClose}
            small
        >
            <SigninForm
                lockedReason={lockedReason}
                lockedContentOwner={lockedContentOwner}
                lockedAction={lockedAction}
            />
        </Drawer>
    )
}

const SigninPage = () => {
    return (
        <Container small>
            <Helmet>
                <title>{config.preTitle}Connexion{config.postTitle}</title>
                <link rel="canonical" href={canonical(createLink("signin"), "", "signin")} />
            </Helmet>
            {/* <PageTitle heading={1}>Connexion</PageTitle> */}
            <SigninForm />
        </Container>
    )
}

export default SigninPage;
